import { useTheme } from "next-themes";
import React, { useEffect, useState } from "react";

export default function RTHMLogo({
    className,
    fillColor,
    link = "/",
}: {
    className?: string;
    fillColor?: string;
    link?: string;
}) {
    const { theme, resolvedTheme } = useTheme();
    const [mounted, setMounted] = useState(false);

    // Use useEffect to handle client-side mounting
    useEffect(() => {
        setMounted(true);
    }, []);

    // Default colors for light and dark modes
    const lightModeColor = "#1A5B77"; // Original blue color
    const darkModeColor = "#4FA3BD"; // Lighter blue for dark mode

    // During SSR and initial render, always use a consistent color (light mode)
    // Only after mounting on client, use the actual theme
    const logoColor =
        fillColor ||
        (mounted && (resolvedTheme === "dark" || theme === "dark")
            ? darkModeColor
            : lightModeColor);

    return (
        <a href={link} className="main-logo" aria-label="Logo link">
            <svg
                width="95"
                height="42"
                viewBox="0 0 95 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`fill-rthm-peach ${className}`}
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M32.443 19.9015V24.0449C31.3329 24.6901 30.0492 25.0977 28.2799 25.0977C25.0535 25.0977 22.8678 23.8411 22.8678 19.6298V11.0036H20.6475H18.1553C15.7019 11.0036 12.7733 13.7205 12.7733 18.0676V24.792H7.5V6.58856H12.7733V10.2564C13.8211 7.80799 15.2683 6.64894 20.2453 6.59087C21.5303 6.4555 22.6816 5.70393 22.8678 4.50088V0H28.1411V4.48419C27.9914 5.78595 27.0415 6.42328 26.5466 6.58531H28.1411V6.58856H32.5124V11.0036H28.1411V18.7808C28.1411 19.9695 28.6615 20.5468 29.8411 20.5468C30.8125 20.5468 31.6798 20.3091 32.443 19.9015ZM52.1244 24.792V13.0073C52.1244 8.83003 49.7999 6.24895 45.8103 6.24895C43.1389 6.24895 41.5778 7.64138 40.3635 9.16965V0H35.0902V24.792H40.3635V14.6375C40.3635 12.1922 41.6471 10.9357 43.6593 10.9357C45.6715 10.9357 46.8511 12.1922 46.8511 14.6375V24.792H52.1244ZM77.9375 19.6281L77.9248 19.6302C77.9248 23.8414 80.1104 25.098 83.3368 25.098C85.1062 25.098 86.3898 24.6905 87.5 24.0452V19.9019C86.7367 20.3094 85.8694 20.5471 84.898 20.5471C83.8244 20.5471 83.2969 20.0688 83.2108 19.0867V12.9394C83.2108 8.55834 80.8517 6.24895 76.9314 6.24895C74.3988 6.24895 72.456 7.2678 70.8254 9.13569C69.854 7.30176 68.0153 6.24895 65.5868 6.24895C62.9155 6.24895 61.3196 7.64138 60.1054 9.16965V6.58856H54.8321V24.792H60.1054V14.6375C60.1054 12.1922 61.3196 10.9357 63.2971 10.9357C65.2746 10.9357 66.3848 12.1922 66.3848 14.6375V24.792H71.6581V14.6375C71.6581 12.1922 72.8723 10.9357 74.8498 10.9357C76.8273 10.9357 77.9375 12.1922 77.9375 14.6375V19.6281Z"
                    fill={logoColor}
                />
                <path
                    d="M10.7311 29.4981C14.4271 29.4981 17.0191 30.8421 17.0191 35.2901C17.0191 39.7541 14.4271 41.0981 10.7311 41.0981H7.91506V29.4981H10.7311ZM10.6991 30.4741H8.98706V40.1221H10.6991C13.3391 40.1221 15.8831 39.5781 15.8831 35.2901C15.8831 31.0021 13.3391 30.4741 10.6991 30.4741ZM25.6121 41.0981H24.5401V29.4981H25.6121V41.0981ZM38.2522 35.9461C37.9482 35.9781 37.6122 35.9781 37.2762 35.9781H34.9402V41.0981H33.8682V29.4981H37.2762C40.0442 29.4981 42.0122 29.9781 42.0122 32.6821C42.0122 34.6341 40.9882 35.4981 39.3722 35.8181L42.1882 41.0981H40.9882L38.2522 35.9461ZM34.9402 30.4741V35.0021H37.4202C39.3242 35.0021 40.8922 34.7621 40.8922 32.7141C40.8922 30.6661 39.3242 30.4741 37.4202 30.4741H34.9402ZM49.6338 29.4981H57.0738V30.4901H50.7058V34.5861H56.6898V35.5941H50.7058V40.1061H57.0738V41.0981H49.6338V29.4981ZM73.8368 37.3701C73.3408 39.6901 71.6608 41.3061 68.9248 41.3061C65.5968 41.3061 63.7728 38.7941 63.7728 35.2901C63.7728 31.7381 65.6608 29.2901 68.9728 29.2901C71.5968 29.2901 73.2928 30.8741 73.7888 33.2261H72.6688C72.2048 31.2901 70.6848 30.3141 68.9408 30.3141C66.6528 30.3141 64.9088 32.0901 64.9088 35.2901C64.9088 38.3781 66.5568 40.2821 68.9408 40.2821C71.1648 40.2821 72.3968 38.8741 72.7168 37.3701H73.8368ZM83.4899 30.4901H79.5859V29.4981H88.4659V30.4901H84.5619V41.0981H83.4899V30.4901Z"
                    fill={logoColor}
                />
            </svg>
        </a>
    );
}
